import { isHex } from "@polkadot/util";
import { encodeAddress, addressEq, base58Decode, checkAddressChecksum } from "@polkadot/util-crypto";
import { bech32 } from "bech32";
export function isLogionIdentityLoc(loc) {
    return loc.locType === 'Identity' && !loc.requesterAccountId && !loc.requesterLocId;
}
export function isLogionDataLoc(loc) {
    return loc.locType !== 'Identity' && (loc.requesterLocId !== undefined && loc.requesterLocId !== null);
}
const ACCOUNT_TYPES = ["Polkadot", "Ethereum", "Bech32"];
export const ETHEREUM_ADDRESS_LENGTH_IN_BITS = 20 * 8;
export const SS58_PREFIX = 2021;
export class AnyAccountId {
    /**
     * Developers should not construct directly this object but call logionApi.queries.getValidAccountId(address, type).
     *
     * @param address
     * @param type
     */
    constructor(address, type) {
        this.address = address;
        this.type = type;
    }
    address;
    type;
    isValid() {
        return this.validate() === undefined;
    }
    validate() {
        if (!ACCOUNT_TYPES.includes(this.type)) {
            return `Unsupported address type ${this.type}`;
        }
        if (this.type === "Ethereum" && !isHex(this.address, ETHEREUM_ADDRESS_LENGTH_IN_BITS)) {
            return `Wrong Ethereum address ${this.address}`;
        }
        else if (this.type === "Polkadot") {
            const validation = this.validPolkadotAccountId();
            return validation !== undefined ? `Wrong Polkadot address ${this.address}: ${validation}` : undefined;
        }
        else if (this.type === "Bech32" && !this.isValidBech32Address()) {
            return `Wrong Bech32 address ${this.address}`;
        }
        else {
            return undefined;
        }
    }
    validPolkadotAccountId() {
        try {
            const decoded = base58Decode(this.address);
            const [isValid, , ,] = checkAddressChecksum(decoded);
            if (isValid) {
                return undefined;
            }
            else {
                return "Invalid decoded address";
            }
        }
        catch (error) {
            return error.message;
        }
    }
    isValidBech32Address(prefix) {
        try {
            if (prefix && !this.address.startsWith(prefix)) {
                return false;
            }
            const decoded = bech32.decode(this.address);
            return prefix === undefined || decoded.prefix === prefix;
        }
        catch (error) {
            return false;
        }
    }
    toValidAccountId() {
        return new ValidAccountId(this);
    }
    toKey() {
        return accountIdToKey(this);
    }
    static parseKey(key) {
        if (key.startsWith(POLKADOT_PREFIX)) {
            return new AnyAccountId(key.substring(POLKADOT_PREFIX.length), "Polkadot");
        }
        else if (key.startsWith(ETHEREUM_PREFIX)) {
            return new AnyAccountId(key.substring(ETHEREUM_PREFIX.length), "Ethereum");
        }
        else if (key.startsWith(BECH32_PREFIX)) {
            return new AnyAccountId(key.substring(BECH32_PREFIX.length), "Bech32");
        }
        else {
            throw new Error("Unsupported key format");
        }
    }
    equals(other) {
        if (!this.isValid() || other === undefined || other === null) {
            return false;
        }
        let otherAnyAccount;
        if (other instanceof AnyAccountId) {
            otherAnyAccount = other;
        }
        else {
            otherAnyAccount = new AnyAccountId(other.address, other.type);
        }
        if (!otherAnyAccount.isValid()) {
            return false;
        }
        if (this.type !== "Polkadot") {
            return this.type === other.type && this.address === other.address;
        }
        else {
            return this.type === other.type && addressEq(this.address, other.address);
        }
    }
}
const POLKADOT_PREFIX = "Polkadot:";
const ETHEREUM_PREFIX = "Ethereum:";
const BECH32_PREFIX = "Bech32:";
function accountIdToKey(accountId) {
    return `${accountId.type}:${accountId.address}`;
}
export class ValidAccountId {
    constructor(accountId) {
        const error = accountId.validate();
        if (error) {
            throw new Error(error);
        }
        this.anyAccountId = new AnyAccountId(ValidAccountId.normalizeAddress(accountId.address, accountId.type), accountId.type);
    }
    anyAccountId;
    get address() {
        return this.anyAccountId.address;
    }
    get type() {
        return this.anyAccountId.type;
    }
    toOtherAccountId() {
        return new OtherAccountId(this);
    }
    toKey() {
        return accountIdToKey(this);
    }
    static parseKey(key) {
        return AnyAccountId.parseKey(key).toValidAccountId();
    }
    equals(other) {
        if (other === undefined || other === null) {
            return false;
        }
        if (other instanceof ValidAccountId) {
            return this.anyAccountId.equals(other.anyAccountId);
        }
        else if (other instanceof AnyAccountId) {
            return this.anyAccountId.equals(other);
        }
        else {
            const anyAddress = new AnyAccountId(other.address, other.type);
            return this.anyAccountId.equals(anyAddress);
        }
    }
    getAddress(prefix) {
        return ValidAccountId.computeAddress(prefix, this.address, this.type);
    }
    static polkadot(address) {
        return new AnyAccountId(address, "Polkadot").toValidAccountId();
    }
    static ethereum(address) {
        return new AnyAccountId(address, "Ethereum").toValidAccountId();
    }
    static bech32(address) {
        return new AnyAccountId(address, "Bech32").toValidAccountId();
    }
    /**
     * Attempt to guess the account type from a given address, and instantiate
     * the corresponding valid account.
     * Warning: this method should NOT be used whenever caller site knows the account type.
     * In this case use {@link polkadot}, {@link ethereum}, {@link bech32} or {@link ValidAccountId:constructor}
     *
     * @param address the address.
     * @returns a valid account or undefined.
     */
    static fromUnknown(address) {
        for (const type of ACCOUNT_TYPES) {
            const account = new AnyAccountId(address, type);
            if (account.isValid()) {
                return account.toValidAccountId();
            }
        }
    }
    static normalizeAddress(address, type) {
        return this.computeAddress(SS58_PREFIX, address, type);
    }
    static computeAddress(prefix, address, type) {
        if (type === 'Polkadot') {
            return encodeAddress(address, prefix);
        }
        return address.toLowerCase();
    }
}
export class OtherAccountId {
    constructor(accountId) {
        if (accountId.type === "Polkadot") {
            throw new Error("Type cannot be Polkadot");
        }
        this.validAccountId = accountId;
    }
    validAccountId;
    get address() {
        return this.validAccountId.address;
    }
    get type() {
        return this.validAccountId.type;
    }
    toKey() {
        return accountIdToKey(this);
    }
    toValidAccountId() {
        return this.validAccountId;
    }
    equals(other) {
        return this.validAccountId.equals(other);
    }
}
